<template>
  <div>
    <v-row>
      <!-- 行×列の編集 -->
      <v-col cols="12" md="8">
        <div class="d-flex align-center mb-2">
          <div class="text-subtitle-2">レイアウト</div>
          <v-spacer />
          <v-btn size="small" variant="outlined" color="primary" @click="addRowDialog = true">
            <v-icon start>mdi-table-row-plus-after</v-icon>
            行を追加
          </v-btn>
        </div>

        <div v-if="draft.rows.length === 0" class="text-caption text-grey py-6 text-center">
          行がありません。「行を追加」から作成してください。
        </div>

        <div v-for="(row, rowIndex) in draft.rows" :key="rowIndex" class="tp-builder-row mb-2">
          <div class="tp-builder-row__toolbar">
            <span class="text-caption text-grey-darken-1">{{ rowIndex + 1 }}行目</span>
            <!--
              列の操作は左、行の操作は右にまとめる。
              どちらも文字ラベルにして、追加と削除を取り違えないようにする。
              列を減らすのはセルを選んで「このセルを削除」(セル設定) から行う。
              数字だけの「列数」セレクトは操作だと読み取れず分かりにくかったため廃止した。
            -->
            <v-btn
              size="x-small"
              variant="text"
              color="primary"
              :disabled="row.cells.length >= maxCellsPerRow"
              @click="addCell(rowIndex)"
            >
              <v-icon start size="14">mdi-table-column-plus-after</v-icon>
              列を追加
            </v-btn>
            <v-spacer />
            <v-btn
              size="x-small"
              variant="text"
              icon="mdi-arrow-up"
              title="1つ上へ"
              :disabled="rowIndex === 0"
              @click="moveRow(rowIndex, -1)"
            ></v-btn>
            <v-btn
              size="x-small"
              variant="text"
              icon="mdi-arrow-down"
              title="1つ下へ"
              :disabled="rowIndex === draft.rows.length - 1"
              @click="moveRow(rowIndex, 1)"
            ></v-btn>
            <!--
              行の削除は取り返しがつかないので、列の増減 (左の列数) や並べ替えと
              間違えないよう、区切って文字ラベルで置く。
            -->
            <v-divider vertical class="mx-2 my-1"></v-divider>
            <v-btn
              size="x-small"
              variant="text"
              color="error"
              @click="removeRow(rowIndex)"
            >
              <v-icon start size="14">mdi-delete-outline</v-icon>
              行を削除
            </v-btn>
          </div>
          <div class="tp-builder-row__cells">
            <!--
              セルの選択はキーボードでもできる必要がある。
              転記ルールのミニグリッドは button なので、同じ「セルを選ぶ」操作で
              作法が割れないよう、こちらも button にする。
            -->
            <button
              v-for="(cell, cellIndex) in row.cells"
              :key="cellIndex"
              type="button"
              class="tp-builder-cell"
              :class="{
                'tp-builder-cell--selected':
                  selected !== null &&
                  selected.rowIndex === rowIndex &&
                  selected.cellIndex === cellIndex,
                'tp-builder-cell--label': cell.type === 'label',
                'tp-builder-cell--empty': isEmptyCell(cell),
                'tp-builder-cell--invalid': cellErrors(cell, rowIndex, cellIndex).length > 0,
              }"
              :style="{ width: `${widthPercents(row.widths)[cellIndex]}%` }"
              @click="select(rowIndex, cellIndex)"
            >
              <div class="tp-builder-cell__name">
                {{ isEmptyCell(cell) ? "空き" : cell.name || "（名称未設定）" }}
              </div>
              <div class="tp-builder-cell__meta">
                <span v-if="isEmptyCell(cell)" :title="'クリックして項目を配置'">＋項目</span>
                <span v-else>{{ cellTypeLabels[cell.type] }}</span>
                <span v-if="cell.required" class="text-error">必須</span>
                <span v-if="!cell.visible">Excel非出力</span>
                <span v-if="cell.phraseKey" class="tp-builder-cell__phrase">
                  文例: {{ cell.phraseKey }}
                </span>
              </div>
            </button>
          </div>
        </div>
      </v-col>

      <!--
        セル設定パネル。
        行が増えると下の行を選んでもパネルが画面外に残り、設定できなくなるため
        画面に追従させる (top はアプリバー40px + 追従するヘッダの分)。
      -->
      <v-col cols="12" md="4" class="tp-builder-panel-col">
        <v-card variant="outlined" class="pa-3">
          <div class="text-subtitle-2 mb-2">セル設定</div>
          <div v-if="!selectedCell" class="text-caption text-grey">
            編集するセルを選択してください。
          </div>
          <template v-else>
            <!--
              空きセルは type='label' なので、項目名を先に置くと
              「名前を入れたのに入力欄にならない (見出しになる)」となる。
              何のセルにするかを先に決めさせる。
            -->
            <div v-if="isEmptyCell(selectedCell)" class="text-caption text-grey-darken-1 mb-2">
              この列に何を置くか選んでください。「見出し」以外を選ぶと入力欄になります。
            </div>

            <v-select
              v-model="selectedCell.type"
              :items="cellTypeItems"
              label="セル型"
              variant="outlined"
              density="compact"
              color="primary"
              class="mb-2"
              hide-details
              @update:model-value="onTypeChange"
            >
              <template #append-inner>
                <info-tip>
                  「見出し」は入力できない項目名だけのセルです。<br />
                  「参照」は上位の帳票の値をそのまま映すセルで、どのセルから引くかは
                  転記ルールで決めます。<br />
                  それ以外は入力欄になります。
                </info-tip>
              </template>
            </v-select>

            <v-text-field
              v-model="selectedCell.name"
              label="項目名"
              variant="outlined"
              density="compact"
              color="primary"
              class="mb-2"
              hide-details
            ></v-text-field>

            <v-text-field
              v-model="selectedCell.itemKey"
              label="項目キー (半角英数字)"
              variant="outlined"
              density="compact"
              color="primary"
              class="mb-2"
              :hint="
                selectedCell.type === 'label'
                  ? '見出しセルは省略できます'
                  : '帳票の値・転記ルールの識別子です'
              "
              persistent-hint
            >
              <template #append-inner>
                <info-tip>
                  書かれた内容・コメント・転記ルールを、このセルに結びつけるための識別子です。<br />
                  <strong>公開したあとに変えると、別のセルとして扱われます</strong>。このセルを
                  指していた転記ルールは外れ、「過去の記載から引用」でも以前の記入は出てきません
                  （作成済みの帳票は作成時のレイアウトのままなので、書いた内容が消えることは
                  ありません）。<br />
                  半角英数字と - _ が使えます。1つのレイアウトの中で重複はできません。
                </info-tip>
              </template>
            </v-text-field>

            <v-text-field
              v-model.number="selectedWidth"
              label="列幅 (比率)"
              type="number"
              min="1"
              variant="outlined"
              density="compact"
              color="primary"
              class="mb-2"
              hide-details
            >
              <template #append-inner>
                <info-tip>
                  同じ行にある他の列との<strong>比率</strong>です。1 と 2 なら後者が2倍の幅になります。<br />
                  画面の見た目とExcelの列幅の両方に効きます。
                </info-tip>
              </template>
            </v-text-field>

            <v-textarea
              v-if="selectedCell.type === 'select'"
              v-model="optionsText"
              label="選択肢 (1行に1つ)"
              rows="4"
              variant="outlined"
              density="compact"
              color="primary"
              class="mb-2"
              hide-details
              @update:model-value="onOptionsChange"
            ></v-textarea>

            <v-text-field
              v-if="isInputCell(selectedCell)"
              v-model="phraseKeyText"
              label="文例グループ"
              variant="outlined"
              density="compact"
              color="primary"
              class="mb-2"
              hint="同じ値を入れたセル同士で文例を共有します（例: 主な活動）"
              persistent-hint
              @update:model-value="onPhraseKeyChange"
            >
              <template #append-inner>
                <info-tip>
                  文例（よく使う定型文）をまとめる単位です。<br />
                  同じ値を入れたセルは同じ文例を共有します。週案の月〜火〜金の「主な活動」を
                  1つにまとめる、といった使い方をします。<br />
                  空にすると、そのセルでは文例を呼び出せません。
                </info-tip>
              </template>
            </v-text-field>

            <!--
              文例は項目横断で整理したいので別タブのままにし、ここからは飛べるようにする
              (転記ルールと同じ往復導線)
            -->
            <div v-if="isInputCell(selectedCell) && selectedCell.phraseKey" class="mb-2">
              <v-btn
                size="x-small"
                variant="text"
                color="primary"
                @click="$emit('open-phrase', selectedCell.phraseKey)"
              >
                <v-icon start size="14">mdi-text-box-multiple-outline</v-icon>
                「{{ selectedCell.phraseKey }}」の文例を編集
              </v-btn>
            </div>

            <v-checkbox
              v-model="selectedCell.required"
              density="compact"
              hide-details
              color="primary"
              :disabled="!isInputCell(selectedCell)"
            >
              <template #label>
                <span class="d-inline-flex align-center ga-1">
                  必須
                  <info-tip>
                    空のままでは提出できなくなります。<br />
                    「Excelに出力する」を外したセルは、必須にしても提出時に検査されません。
                  </info-tip>
                </span>
              </template>
            </v-checkbox>
            <v-checkbox
              v-model="selectedCell.visible"
              density="compact"
              hide-details
              color="primary"
            >
              <template #label>
                <span class="d-inline-flex align-center ga-1">
                  Excelに出力する
                  <info-tip>
                    外すと、Excelではこのセルは<strong>枠だけ残って中身が出ません</strong>
                    （列そのものは詰まりません）。<br />
                    画面では今までどおり入力でき、「Excel非出力」と表示されます。<br />
                    園内のメモなど、印刷して配る帳票に載せたくない項目に使います。
                  </info-tip>
                </span>
              </template>
            </v-checkbox>

            <div v-if="selectedCellErrors.length > 0" class="mt-2">
              <div v-for="error in selectedCellErrors" :key="error" class="text-caption text-error">
                {{ error }}
              </div>
            </div>

            <v-divider class="my-3"></v-divider>
            <div class="d-flex flex-wrap ga-1">
              <v-btn
                size="small"
                variant="text"
                :disabled="isEmptyCell(selectedCell)"
                @click="clearSelectedCell"
              >
                <v-icon start>mdi-eraser</v-icon>
                このセルを空にする
              </v-btn>
              <v-btn
                size="small"
                variant="text"
                color="error"
                :disabled="selectedRowCellCount <= 1"
                @click="removeSelectedCell"
              >
                <v-icon start>mdi-delete-outline</v-icon>
                このセルを削除
              </v-btn>
            </div>
            <div class="text-caption text-grey-darken-1 mt-1">
              「空にする」は列を残したまま項目だけを外します。「削除」は列そのものを詰めます。
            </div>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <!-- 行追加モーダル -->
    <v-dialog v-model="addRowDialog" max-width="560px">
      <v-card>
        <v-card-title>行を追加</v-card-title>
        <v-card-text>
          <div class="text-subtitle-2 mb-1">列構成</div>
          <v-btn-toggle
            v-model="newRowColumns"
            density="compact"
            variant="outlined"
            divided
            mandatory
          >
            <v-btn v-for="preset in rowPresetItems" :key="preset.value" :value="preset.value" size="small">
              {{ preset.title }}
            </v-btn>
          </v-btn-toggle>
          <v-checkbox
            v-model="newRowHasHeader"
            label="先頭を見出し列にする"
            density="compact"
            color="primary"
            hide-details
            class="mt-1"
          ></v-checkbox>

          <div class="text-subtitle-2 mt-3 mb-1">プレビュー</div>
          <div class="tp-preview">
            <div
              v-for="(preview, index) in newRowPreview"
              :key="index"
              class="tp-preview__cell"
              :class="{ 'tp-preview__cell--label': preview.isHeader }"
              :style="{ width: `${preview.widthPercent}%` }"
            >
              {{ preview.label }}
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <div class="text-subtitle-2 mb-1">よく使う構成から作る</div>
          <div class="text-caption text-grey-darken-1 mb-2">
            見出し行と、同じ列構成の空き行を1行ずつ追加します。空きセルをクリックすると項目を配置できます。
          </div>
          <div class="d-flex flex-wrap ga-2">
            <v-btn
              v-for="seed in seedItems"
              :key="seed.value"
              size="small"
              variant="outlined"
              color="primary"
              @click="addSeedRows(seed.value)"
            >
              {{ seed.title }}
            </v-btn>
          </div>

          <div class="text-caption text-grey-darken-1 mt-4">
            追加した行は末尾に入ります。並べ替えは行ごとの矢印で行えます。
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="addRowDialog = false">キャンセル</v-btn>
          <v-btn color="primary" variant="flat" @click="addRow">追加</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import {
  NurseryTeachingPlanCellType,
  NurseryTeachingPlanLayout,
  NurseryTeachingPlanLayoutCell,
} from "chaild-api/lib";
import InfoTip from "@/components/atoms/InfoTip.vue";
import TeachingPlanUtil from "@/utils/TeachingPlanUtil";
import { confirmDanger } from "@/utils/TeachingPlanDialog";
import { cellTypeItems, cellTypeLabels } from "@/constants/teachingPlan";

/** 1行あたりの最大セル数 (レンダラ契約のベース列数と揃える) */
const MAX_CELLS_PER_ROW = 60;

/** 行プリセットのシード種別 (renewal-plan.md C-3) */
type SeedKind = "weekdayMonFri" | "weekdayMonSat" | "term3" | "term4";

const SEED_HEADINGS: Record<SeedKind, string[]> = {
  weekdayMonFri: ["月", "火", "水", "木", "金"],
  weekdayMonSat: ["月", "火", "水", "木", "金", "土"],
  term3: ["第1期", "第2期", "第3期"],
  term4: ["第1期", "第2期", "第3期", "第4期"],
};

/** itemKeyの自動採番。同一ミリ秒でも衝突しないように連番を混ぜる */
let itemKeySequence = 0;
const generateItemKey = (): string => {
  itemKeySequence += 1;
  return `item${Date.now().toString(36)}${itemKeySequence.toString(36)}`;
};

/**
 * 帳票レイアウトの下書きを編集するビルダー。
 * 検証規則は chaild-api の validateTeachingPlanLayout と揃える
 * (保存前に画面側で弾き、サーバの400を出さないようにする)。
 */
export default defineComponent({
  name: "TeachingPlanLayoutBuilder",

  components: {
    InfoTip,
  },

  props: {
    modelValue: {
      type: Object as PropType<NurseryTeachingPlanLayout>,
      required: true,
    },
  },

  emits: ["update:modelValue", "validity-change", "open-phrase"],

  data() {
    return {
      selected: null as { rowIndex: number; cellIndex: number } | null,
      addRowDialog: false,
      newRowColumns: 2,
      /** 先頭を見出し列にするか (モックの行プリセット: 1〜6列 × 見出し列有無) */
      newRowHasHeader: true,
      optionsText: "",
      phraseKeyText: "",
      cellTypeItems,
      cellTypeLabels,
      rowPresetItems: [
        { title: "1列", value: 1 },
        { title: "2列", value: 2 },
        { title: "3列", value: 3 },
        { title: "4列", value: 4 },
        { title: "5列", value: 5 },
        { title: "6列", value: 6 },
      ],
      maxCellsPerRow: MAX_CELLS_PER_ROW,
      seedItems: [
        { title: "曜日（月〜金）", value: "weekdayMonFri" as SeedKind },
        { title: "曜日（月〜土）", value: "weekdayMonSat" as SeedKind },
        { title: "期（3期）", value: "term3" as SeedKind },
        { title: "期（4期）", value: "term4" as SeedKind },
      ],
    };
  },

  computed: {
    draft(): NurseryTeachingPlanLayout {
      return this.modelValue;
    },
    selectedCell(): NurseryTeachingPlanLayoutCell | null {
      if (!this.selected) {
        return null;
      }
      const row = this.draft.rows[this.selected.rowIndex];
      return row ? row.cells[this.selected.cellIndex] || null : null;
    },
    selectedWidth: {
      get(): number {
        if (!this.selected) {
          return 1;
        }
        const row = this.draft.rows[this.selected.rowIndex];
        return row ? row.widths[this.selected.cellIndex] : 1;
      },
      set(value: number) {
        if (!this.selected) {
          return;
        }
        const row = this.draft.rows[this.selected.rowIndex];
        if (!row) {
          return;
        }
        const next = Number(value);
        row.widths[this.selected.cellIndex] =
          Number.isFinite(next) && next > 0 ? next : 1;
        this.emitChange();
      },
    },
    selectedRowCellCount(): number {
      if (!this.selected) {
        return 0;
      }
      const row = this.draft.rows[this.selected.rowIndex];
      return row ? row.cells.length : 0;
    },
    selectedCellErrors(): string[] {
      if (!this.selected || !this.selectedCell) {
        return [];
      }
      return this.cellErrors(this.selectedCell, this.selected.rowIndex, this.selected.cellIndex);
    },
    /** 行追加モーダルの列構成プレビュー */
    newRowPreview(): { label: string; isHeader: boolean; widthPercent: number }[] {
      const widths = this.buildRowWidths(this.newRowColumns, this.newRowHasHeader);
      const percents = TeachingPlanUtil.widthPercents(widths);
      return widths.map((_, index) => {
        const isHeader = this.newRowHasHeader && index === 0;
        return {
          label: isHeader ? "見出し" : "空き",
          isHeader,
          widthPercent: percents[index],
        };
      });
    },
    /** レイアウト全体のエラー (親の保存ボタン制御に使う) */
    layoutErrors(): string[] {
      const errors: string[] = [];
      if (this.draft.rows.length === 0) {
        errors.push("行が1つもありません。");
      }
      this.draft.rows.forEach((row, rowIndex) => {
        row.cells.forEach((cell, cellIndex) => {
          for (const error of this.cellErrors(cell, rowIndex, cellIndex)) {
            errors.push(`${rowIndex + 1}行目: ${error}`);
          }
        });
      });
      return errors;
    },
  },

  watch: {
    layoutErrors: {
      immediate: true,
      handler(errors: string[]) {
        this.$emit("validity-change", errors);
      },
    },
    selected() {
      const cell = this.selectedCell;
      this.optionsText = cell && cell.options ? cell.options.join("\n") : "";
      this.phraseKeyText = cell && cell.phraseKey ? cell.phraseKey : "";
    },
  },

  methods: {
    widthPercents(widths: number[]): number[] {
      return TeachingPlanUtil.widthPercents(widths);
    },
    isInputCell(cell: NurseryTeachingPlanLayoutCell): boolean {
      return TeachingPlanUtil.isInputCell(cell);
    },
    /**
     * 空きセル (項目名のない見出しセル)。
     * レイアウトの列だけ確保して項目は後から配置できるようにするための状態で、
     * サーバの検証も name='' の label セルを許容している。
     */
    isEmptyCell(cell: NurseryTeachingPlanLayoutCell): boolean {
      return cell.type === "label" && !cell.name;
    },
    buildRowWidths(columns: number, hasHeader: boolean): number[] {
      const count = Math.max(1, Math.min(columns, MAX_CELLS_PER_ROW));
      if (!hasHeader) {
        return Array.from({ length: count }, () => 1);
      }
      // 見出し列は本文列より狭くする。ただし列数が増えても
      // 「主な活動」程度が1行で収まる幅は残す (2:3)
      return Array.from({ length: count }, (_, index) => (index === 0 ? 2 : 3));
    },
    buildEmptyCell(): NurseryTeachingPlanLayoutCell {
      return { name: "", type: "label", required: false, visible: true };
    },
    buildLabelCell(name: string): NurseryTeachingPlanLayoutCell {
      return { name, type: "label", required: false, visible: true };
    },
    /** 右端に空きセルを1つ足して、そのまま項目を配置できる状態にする */
    addCell(rowIndex: number) {
      const row = this.draft.rows[rowIndex];
      if (!row || row.cells.length >= MAX_CELLS_PER_ROW) {
        return;
      }
      row.cells.push(this.buildEmptyCell());
      row.widths.push(row.widths[row.widths.length - 1] || 1);
      this.selected = { rowIndex, cellIndex: row.cells.length - 1 };
      this.emitChange();
    },
    emitChange() {
      this.$emit("update:modelValue", this.draft);
    },
    select(rowIndex: number, cellIndex: number) {
      this.selected = { rowIndex, cellIndex };
    },
    cellErrors(
      cell: NurseryTeachingPlanLayoutCell,
      rowIndex: number,
      cellIndex: number
    ): string[] {
      const errors: string[] = [];
      // 見出しセルは名称なし=空きセルとして許容する (列だけ確保した状態)
      if (!cell.name && cell.type !== "label") {
        errors.push("項目名を入力してください。");
      }
      if (cell.type === "label") {
        if (cell.itemKey && !TeachingPlanUtil.isValidItemKey(cell.itemKey)) {
          errors.push("項目キーは半角英数字で始まる64文字以内で入力してください。");
        }
      } else if (!cell.itemKey) {
        errors.push("見出し以外のセルには項目キーが必要です。");
      } else if (!TeachingPlanUtil.isValidItemKey(cell.itemKey)) {
        errors.push("項目キーは半角英数字で始まる64文字以内で入力してください。");
      }
      if (cell.itemKey && this.isDuplicatedItemKey(cell.itemKey, rowIndex, cellIndex)) {
        errors.push("項目キーがレイアウト内で重複しています。");
      }
      if (cell.type === "select") {
        const options = cell.options || [];
        if (options.length === 0) {
          errors.push("選択肢を1つ以上入力してください。");
        } else if (new Set(options).size !== options.length) {
          errors.push("選択肢が重複しています。");
        }
      }
      if (cell.phraseKey && !this.isInputCell(cell)) {
        errors.push("見出し・参照セルには文例グループを設定できません。");
      }
      if (cell.phraseKey && cell.phraseKey.length > 100) {
        errors.push("文例グループは100文字以内で入力してください。");
      }
      return errors;
    },
    isDuplicatedItemKey(itemKey: string, rowIndex: number, cellIndex: number): boolean {
      let count = 0;
      this.draft.rows.forEach((row, ri) => {
        row.cells.forEach((cell, ci) => {
          if (cell.itemKey === itemKey && !(ri === rowIndex && ci === cellIndex)) {
            count += 1;
          }
        });
      });
      return count > 0;
    },
    onTypeChange(type: NurseryTeachingPlanCellType) {
      const cell = this.selectedCell;
      if (!cell) {
        return;
      }
      // labelとreferenceはrequired=false固定・文例グループを持てない
      if (type === "label" || type === "reference") {
        cell.required = false;
        delete cell.phraseKey;
        this.phraseKeyText = "";
      }
      // 空きセルから項目セルに変えたときに項目キーを都度入力させない
      if (type !== "label" && !cell.itemKey) {
        cell.itemKey = generateItemKey();
      }
      if (type === "select") {
        if (!cell.options || cell.options.length === 0) {
          cell.options = [];
          this.optionsText = "";
        }
      } else {
        delete cell.options;
        this.optionsText = "";
      }
      this.emitChange();
    },
    onOptionsChange(value: string) {
      const cell = this.selectedCell;
      if (!cell) {
        return;
      }
      cell.options = value
        .split("\n")
        .map((option) => option.trim())
        .filter((option) => option !== "");
      this.emitChange();
    },
    onPhraseKeyChange(value: string) {
      const cell = this.selectedCell;
      if (!cell) {
        return;
      }
      const trimmed = (value || "").trim();
      if (trimmed === "") {
        delete cell.phraseKey;
      } else {
        cell.phraseKey = trimmed;
      }
      this.emitChange();
    },
    addRow() {
      const widths = this.buildRowWidths(this.newRowColumns, this.newRowHasHeader);
      this.draft.rows.push({
        widths,
        cells: widths.map(() => this.buildEmptyCell()),
      });
      this.addRowDialog = false;
      this.selected = { rowIndex: this.draft.rows.length - 1, cellIndex: 0 };
      this.emitChange();
    },
    /** 「曜日で分ける」「期で分ける」のシード (見出し行 + 同じ列構成の空き行) */
    addSeedRows(kind: SeedKind) {
      const headings = SEED_HEADINGS[kind];
      const widths = this.buildRowWidths(headings.length + 1, true);
      this.draft.rows.push({
        widths: [...widths],
        cells: [this.buildEmptyCell(), ...headings.map((name) => this.buildLabelCell(name))],
      });
      this.draft.rows.push({
        widths: [...widths],
        cells: widths.map(() => this.buildEmptyCell()),
      });
      this.addRowDialog = false;
      this.selected = null;
      this.emitChange();
    },
    /** 列は残したまま項目だけを外す (モック C-2 のセルのクリア) */
    async clearSelectedCell() {
      const cell = this.selectedCell;
      if (!cell || this.isEmptyCell(cell)) {
        return;
      }
      const confirmed = await confirmDanger(
        `「${cell.name || "（名称未設定）"}」を空きセルに戻しますか？`,
        "この項目に入力済みの値は帳票から見えなくなります。",
        "空きセルに戻す"
      );
      // ダイアログを閉じる間に選択が変わっていたら何もしない
      if (!confirmed || this.selectedCell !== cell) {
        return;
      }
      delete cell.itemKey;
      delete cell.options;
      delete cell.phraseKey;
      cell.name = "";
      cell.type = "label";
      cell.required = false;
      cell.visible = true;
      this.optionsText = "";
      this.phraseKeyText = "";
      this.emitChange();
    },
    /** 列そのものを詰める。項目が入っている場合は消えることを確認する */
    async removeSelectedCell() {
      if (!this.selected) {
        return;
      }
      const { rowIndex, cellIndex } = this.selected;
      const row = this.draft.rows[rowIndex];
      const cell = this.selectedCell;
      if (!row || !cell || row.cells.length <= 1) {
        return;
      }
      if (!this.isEmptyCell(cell)) {
        const confirmed = await confirmDanger(
          `「${cell.name || "（名称未設定）"}」の列を削除しますか？`,
          "この項目に入力済みの値は帳票から見えなくなります。",
          "列を削除する"
        );
        // ダイアログを閉じる間に選択が変わると、別のセルを消してしまう
        if (!confirmed || row.cells[cellIndex] !== cell) {
          return;
        }
      }
      row.cells.splice(cellIndex, 1);
      row.widths.splice(cellIndex, 1);
      this.selected = null;
      this.emitChange();
    },
    /**
     * 行の削除。
     * 1行に複数の項目セルが入るため、セル1つの削除より影響が大きい。
     * それなのに確認が軽いと逆転するので、何が消えるかを名前で挙げてから確認する。
     */
    async removeRow(rowIndex: number) {
      const row = this.draft.rows[rowIndex];
      if (!row) {
        return;
      }
      const losing = row.cells.filter((cell) => !this.isEmptyCell(cell) && cell.name);
      const shown = losing.slice(0, 6).map((cell) => `「${cell.name}」`).join("・");
      const rest = losing.length > 6 ? `ほか${losing.length - 6}件` : "";
      const detail =
        losing.length > 0
          ? `消える項目（${losing.length}件）: ${shown}${rest}。これらに入力済みの値は帳票から見えなくなります。`
          : "この行に項目は配置されていません。";
      const confirmed = await confirmDanger(
        `${rowIndex + 1}行目を削除しますか？`,
        detail,
        "行を削除する"
      );
      // ダイアログを閉じる間に並べ替えられていたら、別の行を消してしまう
      if (!confirmed || this.draft.rows[rowIndex] !== row) {
        return;
      }
      this.draft.rows.splice(rowIndex, 1);
      this.selected = null;
      this.emitChange();
    },
    moveRow(rowIndex: number, direction: number) {
      const target = rowIndex + direction;
      if (target < 0 || target >= this.draft.rows.length) {
        return;
      }
      const rows = this.draft.rows;
      const moved = rows.splice(rowIndex, 1)[0];
      rows.splice(target, 0, moved);
      this.selected = null;
      this.emitChange();
    },
  },
});
</script>

<style scoped>
.tp-builder-panel-col {
  align-self: flex-start;
  position: sticky;
  /* アプリバー40px + 追従するレイアウト編集ヘッダ (約64px) */
  top: 108px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}
.tp-builder-row {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.tp-builder-row__toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 8px;
  background-color: #f5f7f8;
  border-bottom: 1px solid #e0e0e0;
}
.tp-builder-row__cells {
  display: flex;
  align-items: stretch;
}
.tp-builder-cell {
  min-width: 0;
  padding: 8px;
  border: none;
  border-right: 1px solid #eceff1;
  background-color: transparent;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
}
.tp-builder-cell:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}
.tp-builder-cell:last-child {
  border-right: none;
}
.tp-builder-cell--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}
.tp-builder-cell--label {
  background-color: #f5f7f8;
}
.tp-builder-cell--empty {
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 5px,
    #eceff1 5px,
    #eceff1 10px
  );
}
.tp-builder-cell--empty .tp-builder-cell__name {
  color: #90a4ae;
  font-weight: 400;
}
.tp-preview {
  display: flex;
  align-items: stretch;
  border-top: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
}
.tp-preview__cell {
  min-width: 0;
  padding: 10px 4px;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  text-align: center;
  font-size: 11px;
  color: #90a4ae;
}
.tp-preview__cell--label {
  background-color: #f5f7f8;
  color: #546e7a;
  font-weight: 700;
}
.tp-builder-cell--invalid {
  background-color: #fff5f5;
}
.tp-builder-cell__name {
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: break-word;
}
.tp-builder-cell__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 10px;
  color: #78909c;
  margin-top: 2px;
}
.tp-builder-cell__phrase {
  color: #1565c0;
}
</style>
